import { Button, Header, Text, Screen, FeatureTooltip } from '@components';
import React, { useCallback, useRef, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, Platform, ActivityIndicator, Image } from 'react-native';
import { useTheme } from '@react-navigation/native';
import { Theme } from '@theme';
import { ScreenContactProps } from '@navigation';
import { useLanguage } from '@context'
import { useScanBusinessCard } from '@hooks';
import { isPlanLimitError } from '@utils';
import { CameraView, CameraType, useCameraPermissions } from 'expo-camera';
import { usePendingCardScanStore } from '../addContacts/store/usePendingCardScanStore';

export const CardScanner: React.FC<ScreenContactProps<'CardScanner'>> = ({ navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)

  const [photo, setPhoto] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { mutateAsync: scanBusinessCard } = useScanBusinessCard();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<CameraView>(null);
  const [permission, requestPermission] = useCameraPermissions();
  const isWeb = Platform.OS === 'web';

  const handleFileSelected = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleCapture = async () => {
    if (isWeb) {
      fileInputRef.current?.click();
      return;
    }

    if (!cameraRef.current) return;
    try {
      const result = await cameraRef.current.takePictureAsync({ base64: false });
      if (result?.uri) setPhoto(result.uri);
    } catch (e) {
      console.error('Failed to take photo:', e);
    }
  };

  const handleScan = async () => {
    if (!photo) return;

    setIsScanning(true);
    setError(null);

    try {
      const result = await scanBusinessCard(photo);
      // Review step: same form as the import page, ending on « Valider et
      // enregistrer la fiche » (previously jumped straight to the profile).
      usePendingCardScanStore.getState().setPending(result);
      navigation.replace('ImportContacts');
    } catch (e: any) {
      console.error('Scan failed:', e);
      // Plan-limit refusals (402) are handled by the mutation's onError, which
      // raises the Premium paywall — don't also show a raw inline error here.
      if (!isPlanLimitError(e)) {
        setError(e?.message || t('errors.generic'));
      }
    } finally {
      setIsScanning(false);
    }
  };

  const handleRetake = () => {
    setPhoto(null);
    setError(null);
  };

  const renderCamera = () => {
    if (!permission?.granted) {
      return (
        <View style={styles.centerContainer}>
          <Text type="body">{t('cardScan.awaitingCamera')}</Text>
          <Button title={t('cardScan.allowCamera')} onPress={requestPermission} hasPaddingHorizontal />
        </View>
      );
    }

    return (
      <View style={{ flex: 1 }}>
        <CameraView
          ref={cameraRef}
          style={StyleSheet.absoluteFill}
          facing="back"
        />
        <TouchableOpacity
          style={{
            position: 'absolute',
            bottom: 40,
            alignSelf: 'center',
            width: 70,
            height: 70,
            borderRadius: 35,
            backgroundColor: 'white',
            borderWidth: 4,
            borderColor: 'rgba(0,0,0,0.3)',
          }}
          onPress={handleCapture}
        />
      </View>
    );
  };

  const renderWebUpload = () => (
    <View style={styles.centerContainer}>
      <input
        ref={fileInputRef as any}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e: any) => {
          const file = e.target.files?.[0];
          if (file) handleFileSelected(file);
        }}
      />
      <div
        onDragOver={(e: any) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e: any) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handleFileSelected(file);
        }}
        style={{
          width: '100%',
          maxWidth: 500,
          minHeight: 250,
          border: `2px dashed ${isDragging ? (theme.colors as any).primary : '#ccc'}`,
          borderRadius: 16,
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
          cursor: 'pointer',
          backgroundColor: isDragging ? 'rgba(0,0,0,0.03)' : 'transparent',
          transition: 'all 0.2s',
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <Text type="body" style={styles.instructionText}>
          {t('cardScan.dragDropPhoto')}
        </Text>
        <Text type="bodySmall" style={styles.instructionText}>
          {t('cardScan.orClickToSelect')}
        </Text>
      </div>
    </View>
  );

  return (
    <Screen>
      <Header
        iconLeft="arrowBack"
        title={t('common.scanBusinessCard')}
        onPressLeft={() => navigation.goBack()}
      />

      <FeatureTooltip
        id="card_scanner"
        title={t('featureTooltip.card_scanner.title')}
        description={t('featureTooltip.card_scanner.description')}
        icon="scan"
      />

      {isScanning && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      )}

      {!isScanning && !photo && (
        <View style={styles.container}>
          {isWeb ? renderWebUpload() : renderCamera()}
        </View>
      )}

      {!isScanning && photo && (
        <View style={styles.container}>
          <Image source={{ uri: photo }} style={styles.preview} resizeMode="contain" />
          {error && (
            <Text type="bodySmall" color={theme.colors.error} style={styles.errorText}>
              {error}
            </Text>
          )}
          <View style={styles.buttonRow}>
            <Button title={t('cardScan.retake')} onPress={handleRetake} hasPaddingHorizontal flex />
            <Button title={t('cardScan.analyze')} onPress={handleScan} hasPaddingHorizontal flex />
          </View>
        </View>
      )}
    </Screen>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.md,
  },
  loadingText: {
    marginTop: theme.spacing.sm,
  },
  instructionText: {
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  preview: {
    flex: 1,
    margin: theme.spacing.md,
    borderRadius: theme.spacing.md,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    padding: theme.spacing.md,
    gap: theme.spacing.md,
  },
  errorText: {
    textAlign: 'center',
    paddingHorizontal: theme.spacing.md,
  },
});
